import { useState } from 'react';

interface InputMappingEditorProps {
  value: Record<string, string> | undefined;
  onChange: (mapping: Record<string, string> | undefined) => void;
  schema?: Record<string, unknown> | null;
  label?: string;
  helpText?: string;
  /**
   * Inspect, do not edit (task 881). The mappings are exactly what a reader
   * opens a node to see — which expression feeds which field — so they render
   * in full, with every input and both row controls disabled.
   */
  readOnly?: boolean;
}

/**
 * Extract field names from a JSON Schema object.
 * Looks for properties keys in a standard JSON Schema structure.
 */
function extractSchemaFieldNames(schema: Record<string, unknown> | null | undefined): string[] {
  if (!schema) return [];
  const properties = schema.properties as Record<string, unknown> | undefined;
  if (!properties || typeof properties !== 'object') return [];
  return Object.keys(properties);
}

export function InputMappingEditor({
  value,
  onChange,
  schema,
  label = 'Input Mapping',
  helpText = 'Use {{nodeId.output.fieldName}} or {{input.fieldName}} as template expressions',
  readOnly = false,
}: InputMappingEditorProps) {
  const schemaFields = extractSchemaFieldNames(schema);
  const hasSchema = schemaFields.length > 0;

  // For dynamic mode: track rows as array of [key, val] pairs
  // Initialize from value or empty
  const [dynamicRows, setDynamicRows] = useState<Array<{ key: string; val: string }>>(() => {
    if (value) {
      return Object.entries(value).map(([key, val]) => ({ key, val }));
    }
    return [];
  });

  // Schema-aware mode: one row per schema field
  if (hasSchema) {
    const currentMapping = value || {};

    const handleSchemaFieldChange = (fieldName: string, expression: string) => {
      const newMapping: Record<string, string> = { ...currentMapping };
      if (expression) {
        newMapping[fieldName] = expression;
      } else {
        delete newMapping[fieldName];
      }
      onChange(Object.keys(newMapping).length > 0 ? newMapping : undefined);
    };

    return (
      <div aria-readonly={readOnly || undefined}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
        <div className="border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-200 dark:divide-gray-600">
          {schemaFields.map((fieldName) => (
            <div key={fieldName} className="flex items-center gap-2 px-3 py-2">
              <span className="text-sm font-mono text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">
                {fieldName}
              </span>
              <span className="text-gray-400 dark:text-gray-500 text-sm flex-shrink-0">=</span>
              <input
                type="text"
                value={currentMapping[fieldName] || ''}
                onChange={readOnly ? undefined : (e) => handleSchemaFieldChange(fieldName, e.target.value)}
                disabled={readOnly}
                placeholder="{{nodeId.output.fieldName}}"
                className="flex-1 text-sm font-mono rounded border-gray-300 dark:border-gray-600
                           focus:border-blue-500 focus:ring-blue-500
                           dark:bg-gray-700 dark:text-white min-w-0"
              />
            </div>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      </div>
    );
  }

  // Dynamic mode: add/delete rows
  const handleRowKeyChange = (index: number, key: string) => {
    const newRows = dynamicRows.map((row, i) => (i === index ? { ...row, key } : row));
    setDynamicRows(newRows);
    emitFromRows(newRows);
  };

  const handleRowValChange = (index: number, val: string) => {
    const newRows = dynamicRows.map((row, i) => (i === index ? { ...row, val } : row));
    setDynamicRows(newRows);
    emitFromRows(newRows);
  };

  const handleAddRow = () => {
    const newRows = [...dynamicRows, { key: '', val: '' }];
    setDynamicRows(newRows);
    emitFromRows(newRows);
  };

  const handleDeleteRow = (index: number) => {
    const newRows = dynamicRows.filter((_, i) => i !== index);
    setDynamicRows(newRows);
    emitFromRows(newRows);
  };

  const emitFromRows = (rows: Array<{ key: string; val: string }>) => {
    const mapping: Record<string, string> = {};
    rows.forEach(({ key, val }) => {
      if (key) {
        mapping[key] = val;
      }
    });
    onChange(Object.keys(mapping).length > 0 ? mapping : undefined);
  };

  return (
    <div aria-readonly={readOnly || undefined}>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <button
          type="button"
          onClick={readOnly ? undefined : handleAddRow}
          disabled={readOnly}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
        >
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Field
        </button>
      </div>

      {dynamicRows.length > 0 ? (
        <div className="border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-200 dark:divide-gray-600">
          {dynamicRows.map((row, index) => (
            <div key={index} className="flex items-center gap-2 px-3 py-2">
              <input
                type="text"
                value={row.key}
                onChange={readOnly ? undefined : (e) => handleRowKeyChange(index, e.target.value)}
                disabled={readOnly}
                placeholder="fieldName"
                className="text-sm font-mono rounded border-gray-300 dark:border-gray-600
                           focus:border-blue-500 focus:ring-blue-500
                           dark:bg-gray-700 dark:text-white w-28 flex-shrink-0"
              />
              <span className="text-gray-400 dark:text-gray-500 text-sm flex-shrink-0">=</span>
              <input
                type="text"
                value={row.val}
                onChange={readOnly ? undefined : (e) => handleRowValChange(index, e.target.value)}
                disabled={readOnly}
                placeholder="{{nodeId.output.fieldName}}"
                className="flex-1 text-sm font-mono rounded border-gray-300 dark:border-gray-600
                           focus:border-blue-500 focus:ring-blue-500
                           dark:bg-gray-700 dark:text-white min-w-0"
              />
              <button
                type="button"
                onClick={readOnly ? undefined : () => handleDeleteRow(index)}
                disabled={readOnly}
                className="flex-shrink-0 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors disabled:cursor-not-allowed disabled:hover:text-gray-400 dark:disabled:hover:text-gray-500"
                aria-label="Remove field"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-md px-3 py-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No mappings defined. Click "Add Field" to add template expressions.
          </p>
        </div>
      )}

      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
    </div>
  );
}
